import { z } from '@hono/zod-openapi';
import { calendarDateSchema, canonicalUtcInstantSchema, competitionRefSchema, matchCardSchema, opaquePublicIdSchema, pageMetaSchema, playerRefSchema, publicEnvelopeSchema, publicSectionSchema, teamRefSchema, } from './football-public.js';
export const footballCoverageSchema = z.object({
    sampleSize: z.number().int().min(0),
    from: calendarDateSchema.nullable(),
    to: calendarDateSchema.nullable(),
    completeness: z.enum(['complete', 'partial', 'unknown']),
    caveats: z.array(z.enum([
        'partial_coverage',
        'field_not_covered',
        'historical_only',
        'membership_unconfirmed',
        'source_limited',
    ])),
    source: z.enum(['api_football', 'ballpeek_projection', 'ballpeek_historical_archive']),
    projectedAt: canonicalUtcInstantSchema,
}).strip();
const formSummarySchema = z.object({
    team: teamRefSchema,
    sampleSize: z.number().int().min(0),
    wins: z.number().int().min(0),
    draws: z.number().int().min(0),
    losses: z.number().int().min(0),
    goalsFor: z.number().int().min(0),
    goalsAgainst: z.number().int().min(0),
}).strip();
const directoryResultSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('team'),
        team: teamRefSchema,
        supportedCompetitions: z.array(competitionRefSchema),
        nextOrRecentMatch: matchCardSchema.nullable(),
    }).strip(),
    z.object({
        type: z.literal('player'),
        player: playerRefSchema,
        latestTeamContext: z.object({
            kind: z.enum(['appearance', 'observation', 'confirmed_membership']),
            team: teamRefSchema,
            observedOn: calendarDateSchema.nullable(),
        }).strip().nullable(),
    }).strip(),
]);
export const directoryResponseSchema = publicEnvelopeSchema(z.object({
    query: z.string(),
    type: z.enum(['all', 'team', 'player']),
    items: z.array(directoryResultSchema),
    pagination: pageMetaSchema,
}).strip());
export const teamProfileResponseSchema = publicEnvelopeSchema(z.object({
    team: teamRefSchema.extend({
        teamType: z.enum(['club', 'national', 'representative', 'unknown']),
        country: z.string().nullable(),
    }).strip(),
    supportedCompetitions: z.array(competitionRefSchema),
    coverage: footballCoverageSchema,
    nextOrLiveMatch: matchCardSchema.nullable(),
    recentMatches: publicSectionSchema(z.object({
        items: z.array(matchCardSchema),
        truncated: z.boolean(),
    }).strip()),
    form: publicSectionSchema(formSummarySchema),
    playerContexts: publicSectionSchema(z.object({
        items: z.array(z.object({
            player: playerRefSchema,
            kind: z.enum(['appearance', 'observation', 'confirmed_membership']),
            observedOn: calendarDateSchema.nullable(),
            validFrom: calendarDateSchema.nullable(),
            validTo: calendarDateSchema.nullable(),
        }).strip()),
        pagination: pageMetaSchema,
    }).strip()),
    links: z.object({
        historyAvailable: z.boolean(),
        headToHeadAvailable: z.boolean(),
    }).strip(),
}).strip());
export const playerProfileResponseSchema = publicEnvelopeSchema(z.object({
    player: playerRefSchema.extend({
        nationality: z.string().nullable(),
        birthDate: calendarDateSchema.nullable(),
    }).strip(),
    coverage: footballCoverageSchema,
    asOfDate: calendarDateSchema,
    appearances: publicSectionSchema(z.object({
        items: z.array(z.object({
            matchId: opaquePublicIdSchema,
            kickoffAt: canonicalUtcInstantSchema,
            competition: competitionRefSchema,
            team: teamRefSchema,
            opponent: teamRefSchema,
            role: z.enum(['starter', 'substitute', 'appeared']),
            minutesPlayed: z.number().int().min(0).nullable(),
        }).strip()),
        pagination: pageMetaSchema,
    }).strip()),
    teamRelationships: publicSectionSchema(z.object({
        items: z.array(z.object({
            team: teamRefSchema,
            kind: z.enum(['appearance', 'observation', 'confirmed_membership']),
            validFrom: calendarDateSchema.nullable(),
            validTo: calendarDateSchema.nullable(),
            boundaryConfidence: z.enum(['not_applicable', 'complete', 'start_unknown', 'end_unknown', 'both_unknown']),
            caveats: z.array(z.enum(['membership_start_unknown', 'membership_end_unknown'])),
        }).strip()),
        truncated: z.boolean(),
    }).strip()),
    historicalStatistics: publicSectionSchema(z.object({
        items: z.array(z.object({
            competition: competitionRefSchema,
            team: teamRefSchema,
            appearances: z.number().int().min(0),
            starts: z.number().int().min(0).nullable(),
            minutes: z.number().int().min(0).nullable(),
            goals: z.number().int().min(0).nullable(),
            assists: z.number().int().min(0).nullable(),
        }).strip()),
        truncated: z.boolean(),
    }).strip()),
}).strip());
export const directoryQuerySchema = z.object({
    q: z.string().trim().min(2).max(100),
    type: z.enum(['all', 'team', 'player']).optional().default('all'),
    page: z.coerce.number().int().min(1).max(1000).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(50).optional().default(20),
}).strict();
export const teamParamsSchema = z.object({ teamId: opaquePublicIdSchema }).strict();
export const teamQuerySchema = z.object({
    playerPage: z.coerce.number().int().min(1).max(1000).optional().default(1),
    playerPageSize: z.coerce.number().int().min(1).max(50).optional().default(20),
    recentMatchLimit: z.coerce.number().int().min(1).max(10).optional().default(5),
}).strict();
export const playerParamsSchema = z.object({ playerId: opaquePublicIdSchema }).strict();
export const playerQuerySchema = z.object({
    appearancePage: z.coerce.number().int().min(1).max(1000).optional().default(1),
    appearancePageSize: z.coerce.number().int().min(1).max(50).optional().default(20),
    at: calendarDateSchema.optional(),
}).strict();
